<template>
  <Select
    v-model="selectedId"
    :options="options"
    option-label="fullName"
    option-value="id"
    :placeholder="languageStore.t('general.selectClient')"
    class="w-full text-sm"
    :loading="loading"
    :disabled="disabled"
    @show="onShow"
    @hide="onHide"
    @change="emit('change')"
  >
    <template #header>
      <div class="p-2">
        <InputText
          v-model="searchQuery"
          :placeholder="languageStore.t('general.search')"
          class="w-full text-sm"
          @input="onSearchInput"
          @click.stop
        />
      </div>
    </template>
  </Select>
</template>

<script lang="ts" setup>
import type { UserClientDto } from '@/models/client'
import * as userClientService from '@/services/userClientService'
import { useClientStore } from '@/stores/ClientStore'
import { useLanguageStore } from '@/stores/LanguageStore'
import { InputText, Select } from 'primevue'
import { ref, computed, onMounted } from 'vue'

const props = defineProps<{
  modelValue?: string | null
  disabled?: boolean
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', value: string | null): void
  (e: 'change'): void
}>()

const languageStore = useLanguageStore()
const clientStore = useClientStore()
const loading = ref(false)
const options = ref<UserClientDto[]>([])
const searchQuery = ref('')

let debounceTimer: ReturnType<typeof setTimeout> | null = null

const selectedId = computed({
  get: () => props.modelValue ?? null,
  set: (v) => emit('update:modelValue', v),
})

async function loadClients(search?: string) {
  loading.value = true
  try {
    const res = await userClientService.getMyClients(search, 10)
    const results = [...res.data]
    const currentId = clientStore.currentClientId
    if (currentId && !results.find((c) => c.id === currentId)) {
      const currentRes = await userClientService.getCurrentClient()
      if (currentRes.data) results.unshift(currentRes.data)
    }
    options.value = results
  } catch {
    // keep existing options
  } finally {
    loading.value = false
  }
}

onMounted(() => loadClients())

function onShow() {
  loadClients(searchQuery.value || undefined)
}

function onHide() {
  searchQuery.value = ''
}

function onSearchInput() {
  if (debounceTimer) clearTimeout(debounceTimer)
  debounceTimer = setTimeout(() => loadClients(searchQuery.value || undefined), 300)
}
</script>
